import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Table, Badge, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { isSuperAdmin } from "../../common/helpers"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import SuperAdminBackLink from "../../components/platform/SuperAdminBackLink"
import moment from "moment"
import { DispatchDetailCategory, DispatchCategoryDrilldown } from "../../types/Indicators.types"

const CATEGORY_COLORS = [
  "#0d6efd", "#198754", "#fd7e14", "#6f42c1", "#dc3545",
  "#0dcaf0", "#ffc107", "#20c997", "#d63384", "#6c757d",
]

interface Props {
  embedded?: boolean
}

interface OrgOption { id: string; name: string }

export default function DispatchCategoryDrilldownPage({ embedded = false }: Props) {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const canSelectOrg = isSuperAdmin(user)

  const [isLoading, setIsLoading] = useState(false)
  const [organizations, setOrganizations] = useState<OrgOption[]>([])
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)
  const [selectedOrgId, setSelectedOrgId] = useState(canSelectOrg ? "" : userOrgId)
  const [byCategory, setByCategory] = useState<DispatchDetailCategory[]>([])
  const [categoryDrilldown, setCategoryDrilldown] = useState<DispatchCategoryDrilldown[]>([])
  const [selectedCategory1, setSelectedCategory1] = useState("")
  const [isLoadingDrilldown, setIsLoadingDrilldown] = useState(false)
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))

  const effectiveOrgId = canSelectOrg ? selectedOrgId : userOrgId

  const loadOrgs = useCallback(async () => {
    if (!canSelectOrg) return
    try {
      setIsLoadingOrgs(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status === "ok") setOrganizations(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingOrgs(false) }
  }, [canSelectOrg])

  const bindData = useCallback(async (start: string, end: string) => {
    if (!effectiveOrgId) return
    try {
      setIsLoading(true)
      setStartDate(start)
      setEndDate(end)
      setCategoryDrilldown([])
      setSelectedCategory1("")
      const params = `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`
      const res = await API.getAction("Indicators/DispatchDetailByCategory", params)
      if (res.data.status === "ok") setByCategory(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoading(false) }
  }, [effectiveOrgId])

  const loadDrilldown = useCallback(async (cat1: string) => {
    if (!effectiveOrgId) return
    try {
      setIsLoadingDrilldown(true)
      const params = `organizationId=${effectiveOrgId}&startDate=${startDate}&endDate=${endDate}&category1=${encodeURIComponent(cat1)}`
      const res = await API.getAction("Indicators/DispatchCategoryDrilldown", params)
      if (res.data.status === "ok") {
        setCategoryDrilldown(res.data.response ?? [])
        setSelectedCategory1(cat1)
      }
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingDrilldown(false) }
  }, [effectiveOrgId, startDate, endDate])

  useEffect(() => { loadOrgs() }, [loadOrgs])

  useEffect(() => {
    setByCategory([])
    setCategoryDrilldown([])
    setSelectedCategory1("")
    if (effectiveOrgId) bindData(startDate, endDate)
  }, [effectiveOrgId])

  const displayRows = selectedCategory1 ? categoryDrilldown : byCategory

  return (
    <Container fluid className="px-4 py-3">
      {!embedded && <SuperAdminBackLink />}

      <h5 className="mb-3">
        <i className="fas fa-layer-group me-2 text-success" />
        {t("Category Drilldown")}
      </h5>

      {canSelectOrg && (
        <Row className="g-2 mb-3">
          <Col md={4} lg={3}>
            <Form.Select
              size="sm"
              value={selectedOrgId}
              onChange={e => setSelectedOrgId(e.target.value)}
              disabled={isLoadingOrgs}
            >
              {!selectedOrgId && <option value="">{t("Select Organization")}</option>}
              {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Form.Select>
          </Col>
        </Row>
      )}

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {canSelectOrg && !selectedOrgId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load dispatch indicators")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <Card className="shadow-sm border-0">
          <Card.Header className="bg-white border-bottom d-flex align-items-center justify-content-between">
            <span className="fw-semibold small">
              {selectedCategory1
                ? <>{t("Category Drilldown")}: <span className="text-primary">{selectedCategory1}</span></>
                : t("Category Drilldown")}
            </span>
            {selectedCategory1 && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => { setCategoryDrilldown([]); setSelectedCategory1("") }}
              >
                <i className="fas fa-arrow-left me-1" />{t("Back")}
              </button>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            {isLoadingDrilldown ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
            ) : (
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">{selectedCategory1 ? t("Subcategory") : t("Category")}</th>
                    <th className="text-end">{t("Total Qty")}</th>
                    <th className="text-end pe-3">{t("Dispatches")}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                  )}
                  {displayRows.map((c, i) => (
                    <tr
                      key={i}
                      style={!selectedCategory1 ? { cursor: "pointer" } : undefined}
                      onClick={!selectedCategory1 ? () => loadDrilldown(c.category) : undefined}
                    >
                      <td className="ps-3">
                        <span className="me-2" style={{ color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}>●</span>
                        {c.category}
                        {!selectedCategory1 && <i className="fas fa-chevron-right ms-2 text-muted small" />}
                      </td>
                      <td className="text-end">{c.totalQuantity.toLocaleString()}</td>
                      <td className="text-end pe-3"><Badge bg="secondary">{c.dispatchCount}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  )
}
